'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faFlame } from '@fortawesome/pro-solid-svg-icons';
import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP } from '../../constants';
import useProfileStrategies from 'src/hooks/strategy/useProfileStrategies';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { HeatmapControls, HEATMAP_DRAWER_ID } from '../HeatmapControls';

interface MapZoomControlsProps {
  engagedId: string;
}

const MapZoomControls: React.FC<MapZoomControlsProps> = ({ engagedId }) => {
  const { zoom, setZoom, showHeatmap } = useProfileStrategies();
  const { toggleDrawer } = useDrawer();

  return (
    <>
      {/* Heatmap Drawer - rendered outside positioned container for full height */}
      <HeatmapControls engagedId={engagedId} />

      {/* Map Controls */}
      <div className="absolute bottom-5 left-5 z-40 inline-flex flex-col justify-start items-start gap-2">
        {/* Flame button */}
        <div className="bg-white rounded-lg shadow-[0px_8px_8px_0px_rgba(0,0,0,0.08)] border border-neutral-200 inline-flex flex-col overflow-hidden">
          <button
            onClick={() => toggleDrawer(HEATMAP_DRAWER_ID)}
            className={`font-semibold select-none
              text-base p-4 flex items-center justify-center rounded-lg transition-all duration-300
              ${
                showHeatmap
                  ? 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800'
                  : 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200'
              }
            `}
            data-testid="heatmap-toggle-button"
            aria-label="Toggle heatmap metrics"
          >
            <Icon icon={faFlame} className="font-bold w-4 h-4" />
          </button>
        </div>

        {/* Zoom controls */}
        <div className="bg-white rounded-lg shadow-[0px_8px_8px_0px_rgba(0,0,0,0.08)] border border-neutral-200 inline-flex flex-col overflow-hidden">
          <button
            onClick={() => zoom < MAX_ZOOM && setZoom(zoom + ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            className="font-semibold transition-all duration-300 select-none text-base bg-neutral-50 text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-500 disabled:bg-neutral-100 disabled:text-neutral-400 p-4 flex items-center justify-center rounded-none"
            aria-label="Zoom in"
          >
            <Icon
              icon={faPlus}
              className="text-neutral-600 font-bold w-4 h-4"
            />
          </button>
          <div className="w-full h-px px-4">
            <div className="h-0 border border-neutral-200"></div>
          </div>
          <button
            onClick={() => zoom > MIN_ZOOM && setZoom(zoom - ZOOM_STEP)}
            disabled={zoom <= MIN_ZOOM}
            className="font-semibold transition-all duration-300 select-none text-base bg-neutral-50 text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-500 disabled:bg-neutral-100 disabled:text-neutral-400 p-4 flex items-center justify-center rounded-none"
            aria-label="Zoom out"
          >
            <Icon
              icon={faMinus}
              className="text-neutral-600 font-bold w-4 h-4"
            />
          </button>
        </div>
      </div>
    </>
  );
};

export default MapZoomControls;
